import { z } from 'zod';

/**
 * **The family relations, in one place** (catalog v12, 2026-09-30).
 *
 * `family_relation.relation` is what **`a` is to `b`**: `grandparent` reads "a is
 * the grandparent of b". A directional relation names its inverse, which is what
 * a reader on `b`'s side sees and what the pair's canonical sort writes when it
 * turns the pair round (`subjecting.ts`); a two-way relation is its own inverse.
 *
 * Words are kinship, never gender: the record seldom knows whether a grandparent
 * is a grandfather, and a guessed "grandmother" is a fact nobody stated.
 *
 * The catalog's `z.enum` in `fact-types.ts` lists the same words inline (the
 * engine's mirror reads that file), and its dedup classes live there too; a
 * server test holds the three in step.
 */
export const FAMILY_RELATIONS = [
  'spouse',
  'former_spouse',
  'partner',
  'parent',
  'child',
  'grandparent',
  'grandchild',
  'great_grandparent',
  'great_grandchild',
  'step_parent',
  'step_child',
  'godparent',
  'godchild',
  'aunt_uncle',
  'niece_nephew',
  'sibling',
  'half_sibling',
  'step_sibling',
  'cousin',
  'parent_in_law',
  'child_in_law',
  'sibling_in_law',
  'in_law',
  'relative',
] as const;
export const FamilyRelation = z.enum(FAMILY_RELATIONS);
export type FamilyRelation = z.infer<typeof FamilyRelation>;

export interface FamilyRelationWords {
  /** What `b` is to `a` — the relation read from the other side. */
  readonly inverse: FamilyRelation;
  /** What `a` is, as a label: "Grandparent". */
  readonly noun: string;
  /** The tie from `a` outwards, lower case, before the other name: "grandparent of". */
  readonly phrase: string;
}

const twoWay = (relation: FamilyRelation, noun: string, phrase: string) =>
  [relation, { inverse: relation, noun, phrase }] as const;

const oneWay = (relation: FamilyRelation, inverse: FamilyRelation, noun: string, phrase: string) =>
  [relation, { inverse, noun, phrase }] as const;

export const FAMILY_RELATION_WORDS: Readonly<Record<FamilyRelation, FamilyRelationWords>> =
  Object.freeze(
    Object.fromEntries([
      twoWay('spouse', 'Spouse', 'married to'),
      twoWay('former_spouse', 'Former spouse', 'formerly married to'),
      twoWay('partner', 'Partner', 'partner of'),
      oneWay('parent', 'child', 'Parent', 'parent of'),
      oneWay('child', 'parent', 'Child', 'child of'),
      oneWay('grandparent', 'grandchild', 'Grandparent', 'grandparent of'),
      oneWay('grandchild', 'grandparent', 'Grandchild', 'grandchild of'),
      oneWay('great_grandparent', 'great_grandchild', 'Great-grandparent', 'great-grandparent of'),
      oneWay('great_grandchild', 'great_grandparent', 'Great-grandchild', 'great-grandchild of'),
      oneWay('step_parent', 'step_child', 'Stepparent', 'stepparent of'),
      oneWay('step_child', 'step_parent', 'Stepchild', 'stepchild of'),
      oneWay('godparent', 'godchild', 'Godparent', 'godparent of'),
      oneWay('godchild', 'godparent', 'Godchild', 'godchild of'),
      oneWay('aunt_uncle', 'niece_nephew', 'Aunt or uncle', 'aunt or uncle of'),
      oneWay('niece_nephew', 'aunt_uncle', 'Niece or nephew', 'niece or nephew of'),
      twoWay('sibling', 'Sibling', 'sibling of'),
      twoWay('half_sibling', 'Half-sibling', 'half-sibling of'),
      twoWay('step_sibling', 'Stepsibling', 'stepsibling of'),
      twoWay('cousin', 'Cousin', 'cousin of'),
      oneWay('parent_in_law', 'child_in_law', 'Parent-in-law', 'parent-in-law of'),
      oneWay('child_in_law', 'parent_in_law', 'Child-in-law', 'child-in-law of'),
      twoWay('sibling_in_law', 'Sibling-in-law', 'sibling-in-law of'),
      twoWay('in_law', 'In-law', 'related by marriage to'),
      twoWay('relative', 'Relative', 'related to'),
    ]) as Record<FamilyRelation, FamilyRelationWords>,
  );

export const isFamilyRelation = (value: unknown): value is FamilyRelation =>
  typeof value === 'string' && (FAMILY_RELATIONS as readonly string[]).includes(value);

/** What `b` is to `a`, or the word itself for a relation that reads the same both ways. */
export const inverseFamilyRelation = (relation: FamilyRelation): FamilyRelation =>
  FAMILY_RELATION_WORDS[relation].inverse;

/**
 * The relation read from one side of the pair: `a`'s own word, or its inverse
 * for a reader standing on `b`.
 */
export const familyRelationFrom = (
  relation: FamilyRelation,
  side: 'a' | 'b',
): FamilyRelationWords =>
  FAMILY_RELATION_WORDS[side === 'a' ? relation : inverseFamilyRelation(relation)];

/**
 * "Ada Moreno is Theo Moreno's grandparent." — a tie read as the family door
 * states it: `a` is `b`'s {relation}.
 */
export const familyTieSentence = (relation: FamilyRelation, a: string, b: string): string =>
  `${a} is ${b}’s ${FAMILY_RELATION_WORDS[relation].noun.toLowerCase()}`;
